import { FC, useState, useMemo, useEffect } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset } from '@opallabs/kuiper'
import { Button, ButtonSize, Icon } from '@opallabs/lapidary'
import { ContentTextInput } from '../../../../../ContentTextInput'
import * as styles from './styles'

interface DocumentCarouselProps {
  assetReference: AssetReference | null
  documentTitle: string
  isPresenting: boolean
  onDocumentTitleChange?: (value: string) => void
  onExpandAsset?: (assetId: number) => void
  onRemoveAsset?: (assetId: number) => void
  readOnly: boolean
  width: number
}

/** Clamp page count between 1 and 10 (LinkedIn's document limit) */
const getPageCount = (pages: unknown): number => {
  if (typeof pages === 'string') {
    const parsed = parseInt(pages, 10)
    return Math.max(1, Math.min(isNaN(parsed) ? 1 : parsed, 10))
  }
  if (typeof pages === 'number') {
    return Math.max(1, Math.min(pages, 10))
  }
  return 1
}

const DocumentHeader: FC<{
  documentTitle: string
  onDocumentTitleChange: (value: string) => void
  pageCount: number
  readOnly: boolean
}> = ({ documentTitle, onDocumentTitleChange, pageCount, readOnly }) => (
  <div css={styles.documentHeader} className="carousel-controls">
    <div css={styles.documentHeaderContent}>
      <ContentTextInput
        ariaLabel="Document title"
        maxRows={1}
        minRows={1}
        onChange={onDocumentTitleChange}
        placeholder="Add document title..."
        readOnly={readOnly}
        style={styles.documentTitleInput}
        value={documentTitle}
      />
      <span css={styles.pageCountLabel}>
        {pageCount} {pageCount === 1 ? 'page' : 'pages'}
      </span>
    </div>
  </div>
)

const NavigationArrows: FC<{
  currentPage: number
  onPrevPage: (e: React.MouseEvent) => void
  onNextPage: (e: React.MouseEvent) => void
  totalPages: number
}> = ({ currentPage, onPrevPage, onNextPage, totalPages }) => (
  <div css={styles.navigationWrapper} className="carousel-controls">
    <Button
      ariaLabel="Previous page"
      disabled={currentPage === 1}
      icon={<Icon glyph="arrow-w" useHypernova width={12} height={12} />}
      name="prev-page"
      onClick={onPrevPage}
      size={ButtonSize.Small}
      styleOverrides={{ button: styles.navButtonOverride }}
      variant="ghost"
    />
    <Button
      ariaLabel="Next page"
      disabled={currentPage === totalPages}
      icon={<Icon glyph="arrow-e" useHypernova width={12} height={12} />}
      name="next-page"
      onClick={onNextPage}
      size={ButtonSize.Small}
      styleOverrides={{ button: styles.navButtonOverride }}
      variant="ghost"
    />
  </div>
)

const PageIndicator: FC<{ currentPage: number; totalPages: number }> = ({
  currentPage,
  totalPages,
}) => (
  <div css={styles.pageIndicator} className="carousel-controls">
    {currentPage} / {totalPages}
  </div>
)

export const DocumentCarousel: FC<DocumentCarouselProps> = ({
  assetReference,
  documentTitle,
  isPresenting,
  onDocumentTitleChange,
  onExpandAsset,
  onRemoveAsset,
  readOnly,
  width,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [isPageLoading, setIsPageLoading] = useState(false)

  const pageCount = getPageCount(assetReference?.meta_data?.pages)
  const hasAsset = typeof assetReference?.uuid === 'string'

  const containerHeight = useMemo(() => {
    if (assetReference?.width && assetReference?.height) {
      const aspectRatio = assetReference.height / assetReference.width
      return Math.round(width * aspectRatio)
    }
    return undefined
  }, [assetReference?.width, assetReference?.height, width])

  // Listen for image load events to clear loading state
  useEffect(() => {
    if (!isPageLoading) return

    const handleLoad = () => setIsPageLoading(false)
    const img = document.querySelector(
      '.document-carousel-asset img'
    ) as HTMLImageElement | null

    if (img) {
      if (img.complete) {
        setIsPageLoading(false)
      } else {
        img.addEventListener('load', handleLoad)
        return () => img.removeEventListener('load', handleLoad)
      }
    }
  }, [isPageLoading, currentPage])

  const handlePrevPage = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (currentPage > 1) {
      setIsPageLoading(true)
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (currentPage < pageCount) {
      setIsPageLoading(true)
      setCurrentPage(currentPage + 1)
    }
  }

  if (!hasAsset) {
    return <div css={styles.emptyPlaceholder}>Upload a PDF to preview</div>
  }

  const containerStyle = containerHeight
    ? ({ '--container-height': `${containerHeight}px` } as React.CSSProperties)
    : undefined

  return (
    <div css={styles.carouselWrapper}>
      <div
        css={[styles.pageContainer, isPageLoading && styles.pageLoading]}
        style={containerStyle}
        className="document-carousel-asset"
      >
        <Asset
          assetReference={assetReference}
          hasAssetOptions={true}
          hasAssetOverlay={!readOnly || isPresenting}
          onEmbiggen={() => {
            if (assetReference?.id) onExpandAsset?.(assetReference.id)
          }}
          onRemove={() => {
            if (assetReference?.id) onRemoveAsset?.(assetReference.id)
          }}
          overrideStyles={{
            assetBase: styles.assetStyles,
          }}
          placeholder={false}
          removeable={!isPresenting}
          transforms={{ page: currentPage, width }}
        />
      </div>

      <DocumentHeader
        documentTitle={documentTitle}
        onDocumentTitleChange={(val) => onDocumentTitleChange?.(val)}
        pageCount={pageCount}
        readOnly={readOnly || isPresenting}
      />

      {pageCount > 1 && !isPresenting && (
        <NavigationArrows
          currentPage={currentPage}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          totalPages={pageCount}
        />
      )}

      {pageCount > 1 && (
        <PageIndicator currentPage={currentPage} totalPages={pageCount} />
      )}
    </div>
  )
}
